import { motion } from 'framer-motion';
import { cn } from '../../lib/cn';
import { useReducedMotion } from '../../hooks/useReducedMotion';

type ScannerTone = 'subtle' | 'normal' | 'intense';

const toneConfig: Record<ScannerTone, { sweep: number; line: number; opacity: number }> = {
  subtle: { sweep: 5.8, line: 8.2, opacity: 0.12 },
  normal: { sweep: 4.4, line: 6.2, opacity: 0.2 },
  intense: { sweep: 3.8, line: 5.2, opacity: 0.2 }
};

export const ScannerOverlay = ({
  className,
  tone = 'normal'
}: {
  className?: string;
  tone?: ScannerTone;
}) => {
  const reducedMotion = useReducedMotion();
  const config = toneConfig[tone];

  return (
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden rounded-inherit', className)} aria-hidden="true">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(183,255,106,0.04)_0%,transparent_40%,rgba(88,247,182,0.04)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(183,255,106,0.04)_1px,transparent_1px)] bg-[length:100%_4px] opacity-15" />

      <motion.div
        className="absolute inset-y-0 left-[-34%] w-[30%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.28),transparent)] blur-sm"
        animate={reducedMotion ? undefined : { x: ['-8%', '430%'], opacity: [0.08, config.opacity, 0.1] }}
        transition={{ duration: config.sweep + 0.8, repeat: Infinity, ease: 'linear' }}
      />

      <motion.div
        className="absolute inset-y-0 left-[-35%] w-[28%] bg-[linear-gradient(90deg,transparent,rgba(88,247,182,0.22),transparent)]"
        animate={reducedMotion ? undefined : { x: ['-10%', '450%'], opacity: [0.06, config.opacity * 0.72, 0.08] }}
        transition={{ duration: config.sweep + 1.7, repeat: Infinity, ease: 'linear' }}
      />

      <motion.div
        className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-lime/80 to-transparent"
        animate={reducedMotion ? undefined : { y: ['-8%', '108%'], opacity: [0.12, 0.62, 0.14] }}
        transition={{ duration: config.line, repeat: Infinity, ease: 'linear' }}
      />

      <motion.div
        className="absolute inset-0 border border-brand-lime/20"
        animate={reducedMotion ? undefined : { opacity: [0.25, 0.4, 0.25] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
};
